<template>
    <div class="relative w-full h-full">
        <transition>
            <div v-if="showLoadingModal"
                class="fixed w-full h-full inset-0 bg-black/30 flex justify-center items-center z-50">
                <div class="w-full h-1/2 flex flex-col justify-center p-3 items-center space-y-5">
                    <div class="flex justify-center space-x-2 mt-3">
                        <span class="w-3 h-3 bg-white shadow-lg rounded-full animate-bounce-wave delay-0"></span>
                        <span class="w-3 h-3 bg-white shadow-lg rounded-full animate-bounce-wave delay-150"></span>
                        <span class="w-3 h-3 bg-white shadow-lg rounded-full animate-bounce-wave delay-300"></span>
                    </div>
                </div>
            </div>
        </transition>

        <form @submit.prevent="handleSubmit"
            class="w-full lg:w-2/3 mx-auto h-full py-5 flex flex-col justify-start items-center space-y-10 px-2 lg:px-0">
            <!-- title and description -->
            <div class="w-full flex flex-col justify-start items-center space-y-2"
                :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
                <div class="text-lg lg:text-2xl w-full lg:mb-2">{{ $t('form_title') }}</div>
                <div class="text-sm lg:text-base w-full">{{ $t('form_description') }}</div>
            </div>

            <!-- inputs -->
            <div class="w-full flex flex-col justify-center items-center space-y-2 lg:space-y-5">
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-5 w-full">
                    <div>
                        <input type="text" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'" v-model="form.lastName"
                            :class="inputClass(input_errors.lastName.error)"
                            :placeholder="$t('placeholders.last_name')" />
                        <p v-if="input_errors.lastName.error" class="text-sm text-red-500 mt-1">
                            {{ $t('errors.last_name') }}
                        </p>
                    </div>

                    <div>
                        <input type="text" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'" v-model="form.firstName"
                            :class="inputClass(input_errors.firstName.error)"
                            :placeholder="$t('placeholders.first_name')" />
                        <p v-if="input_errors.firstName.error" class="text-sm text-red-500 mt-1">
                            {{ $t('errors.first_name') }}
                        </p>
                    </div>

                    <div>
                        <input type="text" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'" v-model="form.phone"
                            :class="inputClass(input_errors.phone.error)" :placeholder="$t('placeholders.phone')" />
                        <p v-if="input_errors.phone.error" class="text-sm text-red-500 mt-1">
                            {{ $t(input_errors.phone.message) }}
                        </p>
                    </div>

                    <div>
                        <select v-model="form.function" name="function" id="function"
                            :class="inputClass(input_errors.function.error)" :placeholder="$t('placeholders.function')">
                            <option value="0" selected>{{ $t('placeholders.function') }}</option>
                            <option :value="f" :selected="form.function === f" v-for="(f, i) in functions" :key="i">
                                {{ $t('function.' + f) }}
                            </option>
                        </select>

                        <p v-if="input_errors.function.error" class="text-sm text-red-500 mt-1">
                            {{ $t('errors.function') }}
                        </p>
                    </div>
                </div>

                <div class="flex  items-start w-full space-x-2 lg:space-x-5">
                    <div v-if="['revendeur', 'distributeur'].includes(form.function)" class="w-3/5 lg:w-2/3">
                        <input type="text" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'" v-model="form.company"
                            class="focus:border-gray-600 transition duration-200 outline-none w-full border border-gray-300 rounded px-3 py-2 text-sm lg:text-base"
                            :placeholder="$t('placeholders.company')" />

                        <p v-if="input_errors.company.error" class="text-sm text-red-500 mt-1">
                            {{ $t('errors.company') }}
                        </p>
                    </div>
                    <div
                        :class="['revendeur', 'distributeur'].includes(form.function) ? 'w-2/5 lg:w-1/3' : 'w-full lg:w-1/2 lg:pr-2.5'">
                        <input type="text" v-model="form.hectares"
                            class="focus:border-gray-600 transition duration-200 outline-none w-full border border-gray-300 rounded px-3 py-2 text-sm lg:text-base"
                            :placeholder="$t('placeholders.hectares')" />
                        <div class="text-xs text-gray-600 mt-1">{{ $t('labels.hectares_note') }}</div>
                    </div>
                </div>
            </div>

            <!-- cultures -->
            <div class="w-full">
                <div :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'"
                    :class="(input_errors.cultures.error ? 'text-red-400 underline ' : '') + 'text-sm lg:text-lg font-medium w-1/2 lg:w-full'">
                    {{ $t('labels.cultures') }}
                </div>
                <div class="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-5">
                    <div class="space-x-2 flex items-baseline justify-start" v-for="(culture, i) in cultures" :key="i">
                        <input type="checkbox" :name="culture.name" :id="culture.id" class="text-primary"
                            :checked="culture.checked" @click="culture.checked = !culture.checked" />
                        <label class="text-gray-600 font-medium" :for="culture.id">
                            {{ $t(culture.label) }}
                        </label>
                    </div>
                </div>
                <p v-if="input_errors.cultures.error" class="text-sm text-red-500 mt-1">
                    {{ $t('errors.cultures') }}
                </p>
            </div>

            <!-- wilaya & commune -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-5 w-full">
                <div>

                    <v-select v-model="form.wilaya" :options="wilayas" @update:modelValue="getCommunes"
                        :reduce="w => w.name" :placeholder="$t('placeholders.wilaya')"
                        :label="$i18n.locale === 'ar' ? 'name_ar' : 'name'" searchPlaceholder="Rechercher..."
                        :class="input_errors.wilaya.error ? 'border border-red-400 ' : ' '">
                        <template #search="{ attributes, events }">
                            <input class="vs__search" v-bind="attributes" v-on="events" :placeholder="!form.wilaya
                                ? (isWilayaSearchFocused ? $t('placeholders.search') : $t('placeholders.wilaya'))
                                : ''" @focus="isWilayaSearchFocused = true" @blur="isWilayaSearchFocused = false" />
                        </template>
                        <template #no-options>
                            <span class="text-gray-500">{{ $t('placeholders.noResultsFound') }}</span>
                        </template>

                    </v-select>

                    <p v-if="input_errors.wilaya.error" class="text-sm text-red-500 mt-1">
                        {{ $t('errors.wilaya') }}
                    </p>
                </div>

                <div>

                    <v-select v-model="form.commune" :options="communes" :reduce="c => c.commune_name_ascii"
                        :placeholder="$t('placeholders.commune')"
                        :label="$i18n.locale === 'ar' ? 'commune_name' : 'commune_name_ascii'"
                        :class="input_errors.commune.error ? 'border border-red-400 ' : ' '">
                        <template #search="{ attributes, events }">
                            <input class="vs__search" v-bind="attributes" v-on="events" :placeholder="!form.commune
                                ? (isCommuneSearchFocused ? $t('placeholders.search') : $t('placeholders.commune'))
                                : ''" @focus="isCommuneSearchFocused = true" @blur="isCommuneSearchFocused = false" />
                        </template>
                        <template #no-options>
                            <span class="text-gray-500">{{ $t('placeholders.noResultsFound') }}</span>
                        </template>

                    </v-select>
                    <!-- <select name="communes" id="communes" v-model="form.commune"
                        :class="inputClass(input_errors.commune.error)">
                        <option value="0" selected>{{ $t('placeholders.commune') }}</option>
                        <option :value="commune.commune_name_ascii" v-for="(commune, i) in communes" :key="i">
                            {{ commune.commune_name_ascii }}
                        </option>
                    </select> -->
                    <p v-if="input_errors.commune.error" class="text-sm text-red-500 mt-1">
                        {{ $t('errors.commune') }}
                    </p>
                </div>
            </div>

            <!-- rules  -->
            <div>
                <div class="flex justify-start items-baseline space-x-2 " :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
                    <input type="checkbox" id="rules" v-model="form.rules"
                        class="ml-2 text-primary focus:ring-0 focus:outline-none" />
                    <label for="rules">{{ $t('i_have_read') }} <a href="#"
                            class="text-blue-500 underline underline-offset-2 hover:cursor-pointer ">{{
                                $t('data_protection_policy') }} </a> {{ $t('i_understand') }}</label>
                </div>
                <p v-if="input_errors.rules.error" class="text-sm text-red-500 mt-1">
                    {{ $t('errors.rules') }}
                </p>
            </div>

            <!-- submit -->
            <div :class="'flex ' + (!hasError ? 'justify-end' : 'justify-between') + ' items-center w-full space-x-2'">

                <!-- Login link -->
                <div class="text-sm lg:text-base text-red-400 font-medium" v-if="hasError">
                    {{ $t('already_have_account') }}

                </div>

                <!-- Submit button -->
                <input type="submit" class="bg-primary w-1/2 lg:w-1/6 text-white text-center rounded py-2"
                    :value="$t('submit')" />


            </div>
        </form>
    </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { wilayas } from '@/data/wilayas'
import { communes as allCommunes } from '@/data/communes'
import { cultures as allCultures } from '@/data/cultures'
import { functions } from '@/data/functions'
import { useRouter } from 'vue-router'
import { requestOtpForRegistration } from '@/api/signup'
import { useSubscriberStore } from '@/stores/subscriber'

import vSelect from "vue3-select";
import "vue3-select/dist/vue3-select.css";

const isWilayaSearchFocused = ref(false);
const isCommuneSearchFocused = ref(false);
const { t } = useI18n()

const cultures = ref([])
onMounted(() => {
    cultures.value = allCultures
    if (subscriberStore.isFilled) {
        populateFormFromStore();
    }
})

const populateFormFromStore = () => {
    form.value = {
        lastName: subscriberStore.lastName || '',
        firstName: subscriberStore.firstName || '',
        phone: subscriberStore.phone || '',
        function: subscriberStore.function || '',
        company: subscriberStore.company || '',
        hectares: subscriberStore.hectares || '',
        type: subscriberStore.type || '',
        wilaya: subscriberStore.wilaya || '',
        commune: subscriberStore.commune || '',
        cultures: subscriberStore.cultures || [],
        rules: subscriberStore.rules || false
    };

    cultures.value = allCultures.map(c => ({
        ...c,
        checked: form.value.cultures.includes(c.id)
    }));



    let wilayaCode = form.value.wilaya.id;
    communes.value = wilayaCode
        ? allCommunes.filter(c => c.wilaya_code === wilayaCode)
        : []
};

const form = ref({
    lastName: '',
    firstName: '',
    phone: '',
    function: 0,
    company: '',
    hectares: '',
    wilaya: null,
    commune: null,
    cultures: [],
    rules: false
})

watch(
    () => form.value.phone,
    (newVal) => {
        form.value.phone = newVal.replace(/\D/g, "");
    }
);

const validatePhone = () => {
    const phone = form.value.phone;

    if (!/^(05|06|07)\d{8}$/.test(phone)) {
        input_errors.value.phone.error = true;
        input_errors.value.phone.message = 'errors.phone_invalid';
    } else {
        input_errors.value.phone.error = false;

    }
};

watch(() => form.value.phone, validatePhone);

watch(() => form.value.hectares,
    (newVal) => {
        form.value.hectares = newVal.replace(/\D/g, "");
    }
);


const input_errors = ref({
    lastName: { error: false },
    firstName: { error: false },
    phone: {
        error: false,
        message: '',
    },
    company: { error: false },
    wilaya: { error: false },
    commune: { error: false },
    cultures: { error: false },
    function: { error: false },
    rules: { error: false }
})




const communes = ref([])
const getCommunes = () => {


    if (form.value.wilaya === null) return;

    form.value.wilaya = getWilayaByName(form.value.wilaya);
    let wilayaCode = form.value.wilaya.id;

    communes.value = wilayaCode
        ? allCommunes.filter(c => c.wilaya_code === wilayaCode)
        : []


    form.value.commune = null;
    input_errors.value.wilaya.error = false;
    input_errors.value.commune.error = false;
}


const getWilayaByName = (name) => {
    return wilayas.find(w => w.name === name);
}



const inputClass = hasError =>
    (hasError ? 'border border-red-400 ' : 'border border-gray-300 ') +
    'focus:border-gray-600 transition duration-200 outline-none w-full rounded px-3 py-2 text-sm lg:text-base'

const showLoadingModal = ref(false)
const subscriberStore = useSubscriberStore()
const router = useRouter()

const handleSubmit = async () => {


    form.value.cultures = cultures.value.filter(c => c.checked).map(c => c.id)

    showLoadingModal.value = true
    setTimeout(async () => {
        if (!validateFields()) {
            try {
                const response = await requestOtpForRegistration(form.value.phone)
                if (response.data.success) {

                    showLoadingModal.value = false
                    subscriberStore.setSubscriberData(form.value)
                    router.push({ name: 'PhoneConfirmation' })
                } else {


                    showLoadingModal.value = false
                    console.log('response.data.message :>> ', response.data.message);
                    // alert(response.data.message || t('alerts.generic_error'))
                }
            } catch (error) {
                showLoadingModal.value = false;
                console.error('Error requesting OTP:', error);

                const field = error?.response?.data?.field;
                const message = error?.response?.data?.message;
                if (field && input_errors.value[field]) {
                    input_errors.value[field].error = true;
                    input_errors.value[field].message = 'errors.'.message || 'errors.phone_exists';
                }
            }
        }



        showLoadingModal.value = false
    }, 1000);



}
const hasError = ref(false);
const validateFields = () => {
    hasError.value = false;

    if (!form.value.lastName) {
        input_errors.value.lastName.error = true
        hasError.value = true
    } else input_errors.value.lastName.error = false

    if (!form.value.firstName) {
        input_errors.value.firstName.error = true
        hasError.value = true
    } else input_errors.value.firstName.error = false

    if (!form.value.phone || input_errors.value.phone.error) {
        input_errors.value.phone.error = true
        input_errors.value.phone.message = 'errors.phone_invalid';

        hasError.value = true
    } else input_errors.value.phone.error = false


    if (!form.value.company && ['revendeur', 'distributeur'].includes(form.value.function)) {
        input_errors.value.company.error = true
        hasError.value = true
    } else input_errors.value.company.error = false

    if (!form.value.function || form.value.function === 0) {
        input_errors.value.function.error = true
        hasError.value = true
    } else input_errors.value.function.error = false

    if (!form.value.wilaya) {
        input_errors.value.wilaya.error = true
        hasError.value = true
    } else input_errors.value.wilaya.error = false

    if (!form.value.commune) {
        input_errors.value.commune.error = true
        hasError.value = true
    } else input_errors.value.commune.error = false

    if (form.value.cultures.length === 0) {
        input_errors.value.cultures.error = true
        hasError.value = true
    } else input_errors.value.cultures.error = false

    if (!form.value.rules) {
        input_errors.value.rules.error = true
        hasError.value = true
    } else input_errors.value.rules.error = false

    return hasError.value;
}
</script>

<style scoped>
@keyframes fade-in {
    from {
        opacity: 0;
        transform: scale(0.95);
    }

    to {
        opacity: 1;
        transform: scale(1);
    }
}

.animate-fade-in {
    animation: fade-in 0.3s ease-out;
}

@keyframes bounce-wave {

    0%,
    80%,
    100% {
        transform: translateY(0);
    }

    40% {
        transform: translateY(-20px);
    }
}

.animate-bounce-wave {
    animation: bounce-wave 1.2s infinite ease-in-out;
}

.delay-0 {
    animation-delay: 0s;
}

.delay-150 {
    animation-delay: 0.15s;
}

.delay-300 {
    animation-delay: 0.3s;
}
</style>
